import React, { useEffect, useState, useCallback } from "react";
import Widget from "../../../components/Elements/Widget";
import { PlantProfile, Field, DataSourceRasterLayer, LayerVisibility } from "../../../types/types";
import { Form, Button } from "react-bootstrap";
import { useStore } from "../../../providers/AppProvider";
import { SETTING_SEASON_YEAR } from "../../../utils/settings";
import { queryDwPointCanopyPropertyData, queryDwPointCanopyStatsData, queryDwPointBloomData, queryDwPointSoilStatsData, queryDwPointSoilDRLData, queryDwPointCultivarStatus } from "../../../utils/dwHelpers";
import { Definition } from "../../../../../data/Definition";
import AboveWidget3SparkCell from "./AboveWidget3SparkCell";
import AboveWidgetImagery from "./AboveWidgetImagery";
import AboveWidgetSoilFertilityCell from "./AboveWidgetSoilFertilityCell";
import AboveWidgetSoilWaterCell from "./AboveWidgetSoilWaterCell";
import { useParams } from 'react-router-dom';
import { MapLayer } from "../../../deckgl/MapLayer";

type Props = {
    plantProfile: PlantProfile | null;
    selectedField: Field | null;
    propertyUnitsData: any | null;
    rasterDataSources: DataSourceRasterLayer[];
    bbCardDataSources: DataSourceRasterLayer[];
    selectedPlantObj: any | null;
    bbCardDataCounter: number;
    setBbCardDataCounter: Function;
    dateArrayIndexSelected: number;
    setDateArrayIndexSelected: Function;
    dateArrayDateStringSelected: string;
    mapLayers: MapLayer[];
    dateSelected: string | null;
    dwBBCardMinMaxData: any;
};

const AboveWidget = ({dateSelected, mapLayers, plantProfile, selectedField, 
    propertyUnitsData, rasterDataSources, bbCardDataSources, bbCardDataCounter,
     setBbCardDataCounter, selectedPlantObj, dateArrayIndexSelected, setDateArrayIndexSelected, 
     dateArrayDateStringSelected, dwBBCardMinMaxData }: Props) => {
    const { state, loading, settings } = useStore();
    const { vp, field } = useParams();
    const [dataLoading, setDataLoading] = useState<boolean>(false);
    const [data, setData] = useState<any | null>(null);
    const [definitionState, setDefinitionState] = useState<Definition | null>(null);

    const [dataCanopyArea, setDataCanopyArea] = useState<any | null>(null);
    const [dataBloom, setDataBloom] = useState<any | null>(null);
    const [dataCanopyVolume, setDataCanopyVolume] = useState<any | null>(null);
    const [dataCPI, setDataCPI] = useState<any | null>(null);
    const [dataCanopyHeight, setDataCanopyHeight] = useState<any | null>(null);

    const [dataCanopyVVI, setDataCanopyVVI] = useState<any | null>(null);
    const [dataCanopyPGI, setDataCanopyPGI] = useState<any | null>(null);
    const [dataCanopyTIR, setDataCanopyTIR] = useState<any | null>(null);

    const [dataSoilStats, setDataSoilStats] = useState<any | null>(null);
    const [dataSoilDRLStats, setDataSoilDRLStats] = useState<any | null>(null);
    const [dataCultivarStatus, setDataCultivarStatus] = useState<any | null>(null);

    const [loadCount, setLoadCount] = useState<number>(0);
    const [selectedTopCell, setSelectedTopCell] = useState<string>("Canopy");
    const [selectedBottomCell, setSelectedBottomCell] = useState<string>("SoilWater");

    const topCellOptions = [
        { id: "Canopy", name: "Canopy", description: "Canopy" },
        { id: "CanopyHealth", name: "Canopy Health", description: "Canopy Health" },
        { id: "CanopySize", name: "Canopy Size", description: "Canopy Size" },
        { id: "BloomIndex", name: "Bloom Index", description: "Bloom Index" },
    ];

    const bottomCellOptions = [
        { id: "SoilWater", name: "Soil Water", description: "...description for Soil Water" },
        { id: "SoilFertility", name: "Soil Fertility", description: "...description for Soil Fertility" },
        { id: "SoilMicronutrients", name: "Soil Micronutrients", description: "...description for Soil Micronutrients" },
        { id: "SoilCationRatios", name: "Soil Cation Ratios", description: "...description for Soil Cation Ratios" },
        { id: "SoilPaste", name: "Soil Paste", description: "...description for Soil Paste" },
        { id: "SoilPasteRatios", name: "Soil Paste Ratios", description: "...description for Soil Paste Ratios" },
        { id: "SoilHealth", name: "Soil Health", description: "...description for Soil Health" },
    ];

    useEffect(() => {
        if (loading || !plantProfile?.plantprofileId) {
            return;
        }

        const controller = new AbortController();

        let definition = state.definitions.find((d: Definition) => d.name.toLowerCase().includes('plant'));
        if (!definition) {
            definition = state.definitions.find((d: Definition) => d.name.toLowerCase().includes('tree'));
        }
        let definitionNames = state.definitions.map((d: Definition) => d.name.toLowerCase());
        if (definitionNames.includes('vine') || definitionNames.includes('tree')) {
            if (selectedField?.crop?.name.toLocaleLowerCase() === 'grapes') {
                definition = state.definitions.find((d: Definition) => d.name.toLowerCase().includes('vine'));
            } else if (selectedField?.crop?.name.toLocaleLowerCase() === 'almonds') {
                definition = state.definitions.find((d: Definition) => d.name.toLowerCase().includes('tree'));
            }
        }

        if (!definition) {
            console.error('Unable to find field definition (at A)');
            return;
        }
        setDefinitionState(definition);
        setDataLoading(true);
        _loadPropertyData(settings[SETTING_SEASON_YEAR], plantProfile.plantprofileId, 'vvic', definition, controller.signal); //
        _loadCanopyStatData(settings[SETTING_SEASON_YEAR], plantProfile.plantprofileId, 'vvic', definition, controller.signal); //
        _loadSoilStatData(settings[SETTING_SEASON_YEAR], plantProfile.plantprofileId, 'vvic', definition, controller.signal); // 
        _loadBloomData(settings[SETTING_SEASON_YEAR], plantProfile.plantprofileId, 'vvic', definition, controller.signal); //

        return () => {
            controller.abort();
        };
    }, [plantProfile]);

    useEffect(() => {
        if (dataCanopyHeight) {
            let selectedDateIndex: number = dataCanopyHeight[3].findIndex((date: string) => date.startsWith(dateArrayDateStringSelected));
            setDateArrayIndexSelected(selectedDateIndex);
        }
    }, [dateArrayDateStringSelected, dataCanopyHeight]);

    useEffect(() => {
        if(dataCanopyHeight){
            console.log("B053024 dateArrayDateStringSelected", dateArrayDateStringSelected);
            console.log("B053024 dataCanopyArea", dataCanopyArea)
            console.log("B053024 dataCanopyHeight", dataCanopyHeight)
            console.log("B053024 dataCanopyVolume", dataCanopyVolume)
            console.log("B053024 dataCanopyVVI", dataCanopyVVI)
            console.log("B053024 dataCanopyPGI", dataCanopyPGI)
            console.log("B053024 dataCanopyTIR", dataCanopyTIR)
            let selectedDateIndex: number = dataCanopyHeight[3].findIndex((date: string) => date.startsWith(dateArrayDateStringSelected));
            console.log("B053024 selectedDateIndex", selectedDateIndex);
            setDateArrayIndexSelected(selectedDateIndex);
        }
    }, [dateArrayDateStringSelected,dataCanopyHeight]);

    const onTopCellTypeChange = (e: any) => {
        setSelectedTopCell(e.target.value);
    };
    const onBottomCellTypeChange = (e: any) => {
        setSelectedBottomCell(e.target.value);
    };

    const _loadPropertyData = async (year: number, _plantprofileId: number, metric: string, definition: Definition, signal: AbortSignal) => {
        const dtStart = `${year}-01-01 00:00:00.00`;
        const dtEnd = `${year}-12-31 23:59:59.59`;
        try {
            const data = await queryDwPointCanopyPropertyData(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            console.log("073024 data",data);
            if (data && data.length) {
                setDataCanopyArea([
                    "Canopy Area",
                    "Canopy Area",
                    data.map((d: any) => d.CanopyArea_value),
                    data.map((d: any) => d.CanopyArea_time ? d.CanopyArea_time : d.CanopyArea_date),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinNumber[0].CanopyArea_min,
                    dwBBCardMinMaxData.dataMaxNumber[0].CanopyArea_max,
                ]);

                setDataCanopyVolume([
                    "Canopy Volume",
                    "Canopy Volume",
                    data.map((d: any) => d.CanopyVolume_value),
                    data.map((d: any) => d.CanopyArea_time ? d.CanopyArea_time : d.CanopyArea_date),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinNumber[0].CanopyVolume_min,
                    dwBBCardMinMaxData.dataMaxNumber[0].CanopyVolume_max,
                ]);

                setDataCPI([
                    "CPI Total",
                    "...description for CPI Total...",
                    data.map((d: any) => d.CPITotal_value),
                    data.map((d: any) => d.CanopyArea_time ? d.CanopyArea_time : d.CPITotal_time),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinNumber[0].CanopyArea_min,
                    dwBBCardMinMaxData.dataMaxNumber[0].CanopyArea_max,
                ]);
            } else {
                setDataCanopyArea(null);
                setDataCanopyVolume(null);
                setDataCPI(null);
            }
            setDataLoading(false);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Failed to load property data:', error);
            }
        }
    };

    const _loadCanopyStatData = async (year: number, _plantprofileId: number, metric: string, definition: Definition, signal: AbortSignal) => {
        const dtStart = `${year}-01-01 00:00:00.00`;
        const dtEnd = `${year}-12-31 23:59:59.59`;
        try {
            const data = await queryDwPointCanopyStatsData(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            console.log("073024 data",data);
            console.log("A080224 dwBBCardMinMaxData", dwBBCardMinMaxData);
            if (data && data.length) {
                setDataCanopyHeight([
                    "Canopy Height",
                    "Canopy Height",
                    data.map((d: any) => d.height_mean),
                    data.map((d: any) => d[`${definition.name ? definition.name : 'plant'}_time`]),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinStats[0].height_mean, //dataMaxNumber[0].CanopyArea_max
                    dwBBCardMinMaxData.dataMaxStats[0].height_mean,
                ]);
                setDataCanopyVVI([
                    "Vegetation Vigor Index",
                    "Vegetation Vigor Index",
                    data.map((d: any) => d.vvi_mean),
                    data.map((d: any) => d[`${definition.name ? definition.name : 'plant'}_time`]),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinStats[0].vvi_mean,
                    dwBBCardMinMaxData.dataMaxStats[0].vvi_mean,
                ]);
                setDataCanopyPGI([
                    "Pigmentation Index",
                    "Pigmentation Index",
                    data.map((d: any) => d.pgi_mean),
                    data.map((d: any) => d[`${definition.name ? definition.name : 'plant'}_time`]),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinStats[0].pgi_mean,
                    dwBBCardMinMaxData.dataMaxStats[0].pgi_mean,
                ]);
                setDataCanopyTIR([
                    "Thermal",
                    "...description for Thermal...",
                    data.map((d: any) => d.tir_mean),
                    data.map((d: any) => d[`${definition.name ? definition.name : 'plant'}_time`]),
                    [dtStart, dtEnd],
                    null, // time min max are not available for this metric
                    null
                ]);
            } else {
                setDataCanopyHeight(null);
                setDataCanopyVVI(null);
                setDataCanopyPGI(null);
                setDataCanopyTIR(null);
            }
            setDataLoading(false);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Failed to load canopy stats data:', error);
            }
        }
    };

    const _loadBloomData = async (year: number, _plantprofileId: number, metric: string, definition: Definition, signal: AbortSignal) => {
        const dtStart = `${year}-01-01 00:00:00.00`;
        const dtEnd = `${year}-12-31 23:59:59.59`;
        try {
            const data = await queryDwPointBloomData(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            console.log("D080524 data",data);
            if (data && data.length) {
                setDataBloom([
                    "Bloom Index",
                    "Bloom Index",
                    data.map((d: any) => d["Bloom Index_value"] ? d["Bloom Index_value"] : null),
                    data.map((d: any) => d["Bloom Index_time"] ? d["Bloom Index_time"] : d["Bloom Index_date"]),
                    [dtStart, dtEnd],
                    dwBBCardMinMaxData.dataMinNumberBI[0]["Bloom Index_min"],
                    dwBBCardMinMaxData.dataMaxNumberBI[0]["Bloom Index_max"],
                ]);
            } else {
                setDataBloom(null);
            }
            setDataLoading(false);
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Failed to load bloom data:', error);
            }
        }
    };

    /* 
    Agriglobe > Barn Owl > 1ZN
    Agriglobe > HEadwaters Investments > Block #4 ONLY

    Soil Fertility
        --> Sulfur == Soluble Sulfur == Soluble Sulfur_mean > 8.2211
    Soil Micronutrients
        Cu == Copper == Copper_mean > 2.3832
        --> Boron == Soluble Boron == Soluble Boron_mean > 0.1597

    Soil Cation Ratios
        BS == Base Saturation == Base Saturation_mean > 100
        EA == Exchangeable Acidity == Exchangeable Acidity_mean > 0
        --> Ca% == Exchangeable Calcium Percentage == Exchangeable Calcium Percentage_mean > 52.2501
        --> Mg% == Exchangeable Magnesium Percentage == Exchangeable Magnesium Percentage_mean > 38.1291
        --> K% == Exchangeable Potassium Percentage == Exchangeable Potassium Percentage_mean > 0.3898
        --> Na% == Exchangeable Sodium Percentage == Exchangeable Sodium Percentage_mean > 9.231
        
    Soil Paste
        SP == Saturation Percentage == Saturation Percentage_mean > 36.2622
        EC == Electrical Conductivity of Saturated Paste Extract == Electrical Conductivity of Saturated Paste Extract_mean > 0.2377
        Ca - sol == Soluble Calcium == Soluble Calcium_mean > 0.3065
        Mg - sol == Soluble Magnesium == Soluble Magnesium_mean > 0.0006
        K - sol == Soluble Potassium == Soluble Potassium_mean > 0.2519
        Na - sol == Soluble Sodium == Soluble Sodium_mean > 0.0031
        Salts == Soluble Salts == Soluble Salts_mean > 0.5621

    Soil Paste Ratios
        X Sol Ca - pct == NG??? Soluble Calcium | Soluble Calcium Magnesium Ratio
        X Sol Mg - pct == NG??? Soluble Magnesium
        X Sol K - pct == NG??? Soluble Potassium | Soluble Potassium Magnesium Ratio
        Sol Na - pct == Soluble Sodium Percentage == Soluble Sodium Percentage_mean > 13.7681
        SAR == Sodium Adsorption Ratio == Sodium Adsorption Ratio_mean > 0.5133

    Soil Health
        X OC == NG - "Organic Carbon" or Soil Organic Carbom" we dont have that ???
        Macro Porosity == Macroporosity == Macroporosity_mean > 30.9344
        Pore Connectivity == Macropore Connectivity (NG - "I think so") == Macropore Connectivity_mean > 16.7011

    RESPONSE
        Base Saturation_mean > 100
        Exchangeable Acidity_mean > 0
        BD_mean > 1.5478
        CEC_mean > 16.9106
        Calcium_mean > 2674.8631
        Copper_mean > 2.3832
        FC_mean > 18.3928
        Iron_mean > 6.1032
        KSAT_mean > 2.0465
        Macropore Connectivity_mean > 16.7011
        Macroporosity_mean > 30.9344
        Magnesium_mean > 297.5146
        Manganese_mean > 19.5258
        Moisture_mean > 10.506
        Nitrogen_mean > 6.0861
        Organic Matter_mean > 0.6312
        PAWC_mean > 8.4129
        PAW_mean > 9.98
        PWP_mean > 9.98
        Phosphorus_mean > 10.0724
        Potassium_mean > 406.4009
        Saturation Percentage_mean > 36.2622
        Soluble Calcium_mean > 0.3065
        Soluble Magnesium_mean > 0.0006
        Soluble Potassium_mean > 0.2519
        Soluble Salts_mean > 0.5621
        Soluble Sodium_mean > 0.0031
        Zinc_mean > 0.2106
        bottom > 10
        pH_mean > 6.2985
        plant_id > "3558017"
        plant_name > "2782"
        plant_time > "2024-07-01T00:00:00.000Z"
        top > 0
        Sodium Adsorption Ratio_mean > 0.5133
        Soluble Sodium Percentage_mean > 13.7681
        Exchangeable Calcium Percentage_mean > 52.2501
        Exchangeable Magnesium Percentage_mean > 38.1291
        Exchangeable Potassium Percentage_mean > 0.3898
        Exchangeable Sodium Percentage_mean > 9.231
        Soluble Boron_mean > 0.1597
        Soluble Sulfur_mean > 8.2211

    */

    const _loadSoilStatData = async (year: number, _plantprofileId: number, metric: string, definition: Definition, signal: AbortSignal) => {
        const dtStart = `${year}-01-01 00:00:00.00`;
        const dtEnd = `${year}-12-31 23:59:59.59`;
        console.log("A112024 definition", definition);
        definition.property.forEach((prop) => {
            //console.log("A112024", prop.name," -- ",prop.property);
            console.log("A112024", prop.property);
            console.log("A112024 -----|", prop.name);
        });
        try {
            const dataStats = await queryDwPointSoilStatsData(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            if (dataStats) {
                let _data = dataStats.sort((a: any, b: any) => a.top - b.top);
                setDataSoilStats(_data);
            }
            const dataDRL = await queryDwPointSoilDRLData(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            if (dataDRL) {
                setDataSoilDRLStats(dataDRL);
            }
            const dataCultivar = await queryDwPointCultivarStatus(_plantprofileId, definition, metric, dtStart, dtEnd, signal);
            if (dataCultivar) {
                setDataCultivarStatus(dataCultivar);
            }
        } catch (error) {
            if (error.name !== 'AbortError') {
                console.error('Failed to load soil stats data:', error);
            }
        }
    };

    return (
        <Widget
            title={`Row: ${plantProfile?.plantprofileRow}${plantProfile?.plantprofileCol ? ",  Plant: " : ""}${plantProfile?.plantprofileCol ? plantProfile?.plantprofileCol : ""}`}
            subTitle={`Id: ${plantProfile?.plantprofileId}${plantProfile?.plantprofileName ? ",  Name: " : ""}${plantProfile?.plantprofileName ? plantProfile?.plantprofileName : ""}`}
            cultivar={dataCultivarStatus && dataCultivarStatus[0] && dataCultivarStatus[0].Cultivar_name ? dataCultivarStatus[0].Cultivar_name : ""}
            status={dataCultivarStatus && dataCultivarStatus[0] && dataCultivarStatus[0].status_name ? dataCultivarStatus[0].status_name : ""}
            renderActions={() => <></>}
        >
            <div
                className="d-flex"
                style={{ paddingBottom: "20px" }}
            >
                <Form.Control as="select"
                    style={{ fontSize: "16pt", paddingLeft: "0px", fontWeight: "bold" }}
                    size="sm"
                    onChange={(e: any) => onTopCellTypeChange(e)}
                    value={selectedTopCell ? selectedTopCell : undefined}>
                    {topCellOptions.map((source: any, index: number) => (
                        <option key={`${source.id}_${index}`} value={source.id}>{source.name}</option>
                    ))
                    }
                </Form.Control>
            </div>
            {(selectedTopCell === "Canopy") &&
                <AboveWidgetImagery
                    loading={dataLoading}
                    metric={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].name}
                    description={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].description}
                    rasterDataSources={rasterDataSources}
                    selectedPlantObj={selectedPlantObj}
                    selectedField={selectedField}
                    bbCardDataSources={bbCardDataSources}
                    bbCardDataCounter={bbCardDataCounter}
                    setBbCardDataCounter={setBbCardDataCounter}
                    dataCanopyArea={dataCanopyArea}
                    dataCanopyHeight={dataCanopyHeight}
                    dataCanopyVolume={dataCanopyVolume}
                    propertyUnitsData={propertyUnitsData}
                    dateArrayIndexSelected={dateArrayIndexSelected}
                    dateArrayDateStringSelected={dateArrayDateStringSelected}
                    dataCanopyVVI={dataCanopyVVI}
                    dataCanopyPGI={dataCanopyPGI}
                    dataCanopyTIR={dataCanopyTIR}
                    dataCPI={dataCPI}
                    mapLayers={mapLayers}
                />
            }{(selectedTopCell === "CanopySize") &&
                <AboveWidget3SparkCell
                    loading={dataLoading}
                    metric={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].name}
                    description={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].description}
                    dataSpark1={dataCanopyArea}
                    dataSpark2={dataCanopyHeight}
                    dataSpark3={dataCanopyVolume}
                    propertyUnitsData={propertyUnitsData}
                    dateSelected={dateSelected}
                />
            }{
            }{(selectedTopCell === "BloomIndex") &&
                <AboveWidget3SparkCell
                    loading={dataLoading}
                    metric={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].name}
                    description={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].description}
                    dataSpark1={dataBloom}
                    dataSpark2={null}
                    dataSpark3={null}
                    propertyUnitsData={propertyUnitsData}
                    dateSelected={dateSelected}
                />
            }{
                (selectedTopCell === "CanopyHealth") &&
                <AboveWidget3SparkCell
                    loading={dataLoading}
                    metric={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].name}
                    description={topCellOptions.filter((d: any) => d.id === selectedTopCell)[0].description}
                    dataSpark1={dataCanopyVVI}
                    dataSpark2={dataCanopyPGI}
                    dataSpark3={dataCanopyTIR}
                    propertyUnitsData={propertyUnitsData}
                    dateSelected={dateSelected}
                />
            }
            <hr />
            <div
                className="d-flex"
                style={{ paddingBottom: "20px" }}
            >
                <Form.Control as="select"
                    style={{ fontSize: "16pt", paddingLeft: "0px", fontWeight: "bold" }}
                    size="sm"
                    onChange={(e: any) => onBottomCellTypeChange(e)}
                    value={selectedBottomCell ? selectedBottomCell : undefined}>
                    {bottomCellOptions.map((source: any, index: number) => (
                        <option key={`${source.id}_${index}`} value={source.id}>{source.name}</option>
                    ))
                    }
                </Form.Control>
            </div>
            {((selectedBottomCell === "SoilFertility") || (selectedBottomCell === "SoilPaste") || (selectedBottomCell === "SoilPasteRatios") || (selectedBottomCell === "SoilCationRatios") || (selectedBottomCell === "SoilMicronutrients") || (selectedBottomCell === "SoilHealth")) &&
                <AboveWidgetSoilFertilityCell
                    definition={definitionState}
                    loading={dataLoading}
                    metric={bottomCellOptions.filter((d: any) => d.id === selectedBottomCell)[0].name}
                    description={bottomCellOptions.filter((d: any) => d.id === selectedBottomCell)[0].description}
                    dataSoilStats={dataSoilStats}
                    dataSoilDRLStats={dataSoilDRLStats}
                    propertyUnitsData={propertyUnitsData}
                    setDataSoilStats={setDataSoilStats}
                />
            }{
                (selectedBottomCell === "SoilWater") &&
                <AboveWidgetSoilWaterCell
                    definition={definitionState}
                    loading={dataLoading}
                    metric={bottomCellOptions.filter((d: any) => d.id === selectedBottomCell)[0].name}
                    description={bottomCellOptions.filter((d: any) => d.id === selectedBottomCell)[0].description}
                    dataSoilStats={dataSoilStats}
                    dataSoilDRLStats={dataSoilDRLStats}
                    propertyUnitsData={propertyUnitsData}
                />
            }
        </Widget>
    );
};

export default AboveWidget;
